import { type AddHighlightOptions, type GeometryResolver } from '@databases/highlights';
import { type TableV2GeometryTarget } from '@databases/view-layout-table-v2/dom';

export type RegionKey =
  | 'body'
  | 'header'
  | 'prefix'
  | 'corner'
  | 'frozenBody'
  | 'frozenHeader';

export interface HighlightRegion {
  readonly key: RegionKey;
  readonly host: HTMLElement;
  readonly start: number;
  readonly end: number;
  readonly x: number;
  readonly width: number;
  readonly height: number;
  readonly header: boolean;
  readonly prefix: boolean;
}

/**
 * The two axes compose independently. Missing frozen hosts allocate nothing;
 * committed renderer geometry supplies the effective count and matching hosts.
 */
export function getHighlightRegions(target: TableV2GeometryTarget): HighlightRegion[] {
  const layout = target.highlights;

  if (!layout) {
    return [];
  }

  const count = layout.frozenLeadingColumnCount;
  const boundary = count
    ? layout.columns[count - 1].start + layout.columns[count - 1].width
    : 0;
  const last = layout.columns.length - 1;
  const regions: HighlightRegion[] = [];

  const add = (region: Omit<HighlightRegion, 'host' | 'height'>) => {
    const host = layout[region.key];
    const height = region.header ? layout.headerHeight : target.totalHeight;

    if (host && region.width > 0 && height > 0) {
      regions.push({ ...region, host, height });
    }
  };

  for (const header of [false, true]) {
    add({
      key: header ? 'header' : 'body',
      header,
      prefix: false,
      start: count,
      end: last,
      x: boundary,
      width: target.totalWidth - boundary
    });

    if (count > 0) {
      add({
        key: header ? 'frozenHeader' : 'frozenBody',
        header,
        prefix: false,
        start: 0,
        end: count - 1,
        x: 0,
        width: boundary
      });
    }

    if (layout.prefixWidth > 0) {
      add({
        key: header ? 'corner' : 'prefix',
        header,
        prefix: true,
        start: 0,
        end: last,
        x: 0,
        width: layout.prefixWidth
      });
    }
  }

  return regions;
}

export interface RegionProjection {
  readonly options: AddHighlightOptions;
  readonly open: {
    readonly top: boolean;
    readonly right: boolean;
    readonly bottom: boolean;
    readonly left: boolean;
  };
}

/**
 * Clip logical columns before resolving pixels. Borders describe the original
 * range, not the viewport: scrolling must never manufacture a selection edge.
 */
export function projectRegion({
  region,
  options,
  includeChrome,
  target
}: {
  region: HighlightRegion;
  options: AddHighlightOptions;
  includeChrome: boolean;
  target: TableV2GeometryTarget;
}): RegionProjection | null {
  const { mode, range } = options;

  if (mode === 'none') {
    return null;
  }

  if (region.header && (!includeChrome || (mode !== 'column' && mode !== 'container'))) {
    return null;
  }

  if (region.prefix && (!includeChrome || (mode !== 'row' && mode !== 'container'))) {
    return null;
  }

  const fullWidth = mode === 'row' || mode === 'container';
  const min = fullWidth ? 0 : Math.min(range.anchor.col, range.lead.col);
  const max = fullWidth
    ? target.model.columns.length - 1
    : Math.max(range.anchor.col, range.lead.col);
  const start = Math.max(min, region.start);
  const end = Math.min(max, region.end);

  if (!region.prefix && start > end) {
    return null;
  }

  const layout = target.highlights;
  const frozen = region.key === 'frozenBody' || region.key === 'frozenHeader';
  const headerHost = region.prefix
    ? layout?.corner
    : frozen
      ? layout?.frozenHeader
      : layout?.header;
  const bodyHost = region.prefix
    ? layout?.prefix
    : frozen
      ? layout?.frozenBody
      : layout?.body;

  const includesPrefix =
    includeChrome &&
    fullWidth &&
    (layout?.prefixWidth ?? 0) > 0 &&
    !!(region.header ? layout?.corner : layout?.prefix);
  const includesHeader =
    includeChrome &&
    (mode === 'column' || mode === 'container') &&
    (layout?.headerHeight ?? 0) > 0 &&
    !!headerHost;

  return {
    options: {
      ...options,
      range: {
        ...range,
        anchor: { ...range.anchor, col: start },
        lead: { ...range.lead, col: end }
      }
    },
    open: {
      top: !region.header && includesHeader,
      bottom: region.header && target.totalHeight > 0 && !!bodyHost,
      left:
        !region.prefix && (min < region.start || (includesPrefix && region.start === 0)),
      right: region.prefix ? target.totalWidth > 0 : max > region.end
    }
  };
}

/**
 * Each physical renderer sees local coordinates in its host. Both frozen and
 * scrolling regions use the renderer's authoritative row/column geometry.
 */
export function createRegionResolver(
  read: () => { target: TableV2GeometryTarget; region: HighlightRegion } | undefined
): GeometryResolver {
  const column = (index: number) => {
    const current = read();
    const field = current?.target.model.columns[index];
    const rect = field && current?.target.geometry.getColumnRect(field.fieldId);

    return current && rect
      ? {
          ...rect,
          x: rect.x - current.region.x,
          y: 0,
          height: current.region.height
        }
      : null;
  };

  const row = (index: number) => {
    const current = read();
    const entry = current?.target.model.rows[index];
    const rect = entry && current?.target.geometry.getRowRect(entry.entryId);

    return current && rect ? { ...rect, x: 0, width: current.region.width } : null;
  };

  return {
    getScrollContainer: () => read()?.target.scrollContainer ?? null,

    getCellBounds(rowIndex, colIndex) {
      const r = row(rowIndex);
      const c = column(colIndex);

      return r && c ? { x: c.x, y: r.y, width: c.width, height: r.height } : null;
    },

    getRowBounds: row,
    getColumnBounds: column,

    getContainerBounds() {
      const current = read();

      return current
        ? {
            x: 0,
            y: 0,
            width: current.region.width,
            height: current.region.height
          }
        : null;
    }
  };
}
